import { useRef, useEffect, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import { RichTextContent } from '@vibesites/runtime/react';
import { EditableSlotGroup } from '@vibesites/runtime/react';
import {
  VibesitesImage,
  getMediaUrl,
  parseStorageKey,
  videoThumbStorageKey,
} from '@vibesites/ui-primitives';

const bgDef: SlotGroupDefinition = {
  label: 'Background Media',
  description: 'If a video is set, it plays as the background. Otherwise the image is used.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    video: {
      label: 'Video',
      type: 'video',
      defaultField: 'property_featured_video',
    },
  },
};

// Three bottom "quick link" cards. Each card is a single clickable tile with a
// heading, a short blurb, and an arrow affordance.
const card1Def: SlotGroupDefinition = {
  label: 'Card · Amenities',
  fields: {
    heading: { label: 'Heading', type: 'text', defaultValue: 'Cool Amenities' },
    text: {
      label: 'Description',
      type: 'text',
      defaultValue: 'Explore the resort-style pool, fitness center, dog park and more.',
    },
    url: { label: 'URL', type: 'url', defaultValue: '/amenities' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const card2Def: SlotGroupDefinition = {
  label: 'Card · Rooms',
  fields: {
    heading: { label: 'Heading', type: 'text', defaultValue: 'Luxury Rooms' },
    text: {
      label: 'Description',
      type: 'text',
      defaultValue: 'Elevate your everyday living experience.',
    },
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const card3Def: SlotGroupDefinition = {
  label: 'Card · Apply',
  fields: {
    heading: { label: 'Heading', type: 'text', defaultValue: 'Apply and Save' },
    text: {
      label: 'Description',
      type: 'text',
      defaultValue: 'Save on rent, plus waived fees for a limited time.',
    },
    url: { label: 'URL', type: 'url', defaultValue: '/apply-now' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

function ArrowIcon() {
  return (
    <svg viewBox="0 0 40 16" fill="none" aria-hidden="true" className="hero-card-arrow">
      <path d="M0 8h37M30 1l7 7-7 7" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

interface CardLink {
  heading?: string;
  text?: string;
  url?: string;
  _target?: string;
}

function CardBody({
  card,
  defaultUrl,
  defaultHeading,
  defaultText,
}: {
  card: CardLink;
  defaultUrl: string;
  defaultHeading: string;
  defaultText: string;
}) {
  return (
    <a
      href={card.url || defaultUrl}
      target={card._target || '_self'}
      className="hero-card"
    >
      <div className="hero-card-head">
        <span className="hero-card-heading">{card.heading || defaultHeading}</span>
        <ArrowIcon />
      </div>
      <span className="hero-card-text">{card.text || defaultText}</span>
    </a>
  );
}

export function Hero() {
  const bg = useSlotGroup('hero.bg_image', bgDef);
  const card1 = useSlotGroup('hero.card_1', card1Def);
  const card2 = useSlotGroup('hero.card_2', card2Def);
  const card3 = useSlotGroup('hero.card_3', card3Def);

  const showVideo = !!bg.video;
  // Derive poster from the bound video so overrides stay correct; fall back to image.
  const thumbKey = videoThumbStorageKey(parseStorageKey(bg.video || null));
  const poster =
    (thumbKey ? getMediaUrl(thumbKey, { width: 1920 }) : null) || bg.image || '';

  // Parallax refs (image-only backgrounds)
  const sectionRef = useRef<HTMLElement>(null);
  const imgWrapRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [reducedMotion, setReducedMotion] = useState(false);

  // Respect prefers-reduced-motion: drop autoplay (pause() alone loses the race
  // if the video hasn't buffered yet) and pause anything already playing.
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => {
      setReducedMotion(mq.matches);
      if (mq.matches) videoRef.current?.pause();
    };
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="montserrat"]')) return;
    if (!document.querySelector('link[data-vibe-font-preconnect="google"]')) {
      const preconnect1 = document.createElement('link');
      preconnect1.rel = 'preconnect';
      preconnect1.href = 'https://fonts.googleapis.com';
      preconnect1.setAttribute('data-vibe-font-preconnect', 'google');
      document.head.appendChild(preconnect1);
      const preconnect2 = document.createElement('link');
      preconnect2.rel = 'preconnect';
      preconnect2.href = 'https://fonts.gstatic.com';
      preconnect2.crossOrigin = 'anonymous';
      preconnect2.setAttribute('data-vibe-font-preconnect', 'gstatic');
      document.head.appendChild(preconnect2);
    }
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400;1,700&display=swap';
    link.setAttribute('data-vibe-font', 'montserrat');
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    if (showVideo) return;
    const section = sectionRef.current;
    const imgWrap = imgWrapRef.current;
    if (!section || !imgWrap) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const onScroll = () => {
      const rect = section.getBoundingClientRect();
      const scrolled = -rect.top;
      if (scrolled < -window.innerHeight || scrolled > section.offsetHeight) return;
      imgWrap.style.transform = `translateY(${scrolled * 0.3}px)`;
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [showVideo]);

  return (
    <section ref={sectionRef} className="hero-section relative w-full overflow-hidden">
      <EditableSlotGroup groupPath="hero.bg_image" definition={bgDef}>
        {showVideo ? (
          <div className="hero-bg-media absolute inset-0 bg-neutral-500">
            {/* Decorative: the overlaid headline carries the content. */}
            <video
              ref={videoRef}
              className="absolute inset-0 h-full w-full object-cover"
              src={bg.video || undefined}
              poster={poster}
              autoPlay={!reducedMotion}
              muted
              loop
              playsInline
              aria-hidden
            />
          </div>
        ) : (
          /* Parallax + Ken Burns animated background */
          <div
            ref={imgWrapRef}
            className="hero-bg-media absolute h-[140%]"
            style={{ top: '-20%', left: '-1%', right: '-1%', willChange: 'transform' }}
          >
            {bg.image ? (
              <VibesitesImage
                url={bg.image}
                alt=""
                priority
                sizes="100vw"
                className="h-full w-full object-cover"
                style={{
                  animation: 'heroKenBurns 30s ease-in-out infinite alternate',
                  willChange: 'transform',
                  transformOrigin: 'bottom right',
                  imageRendering: 'high-quality' as any,
                }}
              />
            ) : (
              <div className="h-full w-full bg-[var(--vibe-color-text)]" />
            )}
          </div>
        )}
      </EditableSlotGroup>

      {/* Light gray tint + soft dark shade for text contrast */}
      <div className="hero-tint absolute inset-0" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-black/10 to-black/10" />

      {/* Content — centered stack with the quick-link cards pinned to the bottom. */}
      <div className="hero-content relative z-10 flex h-full flex-col px-6 sm:px-8 text-center text-white">
        <div className="hero-center flex flex-1 flex-col items-center justify-center md:justify-end">
          <RichTextContent
            path="hero.title"
            label="Title"
            defaultValue="<p>The Ultimate Student Living Experience</p>"
            as="h1"
            className="hero-entrance hero-entrance-2 hero-title"
          />

          <RichTextContent
            path="hero.subtitle"
            label="Subtitle"
            defaultValue='<p><span data-type="field-token" data-field="property_bedrooms_range"></span> Bedroom Apartments in <span class="hero-subtitle-location"><span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span></span></p>'
            as="h2"
            className="hero-entrance hero-entrance-2 hero-subtitle"
          />
        </div>

        <div className="hero-cards hero-entrance hero-entrance-3">
          <EditableSlotGroup groupPath="hero.card_1" definition={card1Def} className="hero-card-cell">
            <CardBody
              card={card1}
              defaultUrl="/amenities"
              defaultHeading="Cool Amenities"
              defaultText="Explore the resort-style pool, fitness center, dog park and more."
            />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="hero.card_2" definition={card2Def} className="hero-card-cell">
            <CardBody
              card={card2}
              defaultUrl="/floor-plans"
              defaultHeading="Luxury Rooms"
              defaultText="Elevate your everyday living experience."
            />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="hero.card_3" definition={card3Def} className="hero-card-cell">
            <CardBody
              card={card3}
              defaultUrl="/apply-now"
              defaultHeading="Apply and Save"
              defaultText="Save on rent, plus waived fees for a limited time."
            />
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        @keyframes heroKenBurns {
          0% { transform: scale(1.01) translate(0, 0); }
          100% { transform: scale(1.05) translate(-0.8%, -0.5%); }
        }
        @keyframes heroEntrance {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hero-entrance {
          animation: heroEntrance 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .hero-entrance-1 { animation-delay: 0.15s; }
        .hero-entrance-2 { animation-delay: 0.35s; }
        .hero-entrance-3 { animation-delay: 0.55s; }

        /* Fill the viewport — title/subtitle center in the space above the
           cards, and the cards sit at the fold rather than below it. */
        .hero-section {
          isolation: isolate;
          height: 100vh;
          height: 100svh;
          height: 100dvh;
          min-height: 100vh;
          min-height: 100svh;
          min-height: 100dvh;
        }

        /* Light gray wash over the video/photo — lighter than the prior 40% primary tint. */
        .hero-tint {
          background: color-mix(in srgb, #6b7280 18%, transparent);
        }

        /* Leave room for the fixed floating navbar at the top and the cards
           at the bottom. */
        .hero-content {
          padding-top: calc(var(--offer-banner-height, 0px) + var(--top-bar-height, 0px) + 7rem);
          padding-bottom: 2rem;
        }
        @media (min-width: 768px) {
          .hero-content {
            padding-bottom: 2.5rem;
          }
        }

        .hero-title {
          font-family: var(--vibe-font-heading), sans-serif;
          font-size: 26px;
          font-weight: 700;
          line-height: 1.05;
          letter-spacing: 0;
          color: #fff;
          text-shadow: 0 2px 24px rgba(0, 0, 0, 0.25);
          margin: 0 auto 1.25rem;
          /* Mobile: keep the narrow width so the default title
             "The Ultimate Student Living Experience" wraps to two lines
             (long single-line titles read awkwardly on phones). */
          max-width: 22ch;
          /* If a custom title wraps anywhere, balance the lines so no
             single line looks orphaned. */
          text-wrap: balance;
        }
        .hero-title p,
        .hero-title span {
          font: inherit;
          color: inherit;
          text-shadow: inherit;
          margin: 0;
        }
        @media (min-width: 768px) {
          /* Tablet & desktop: open up the width so the default title fits on
             ONE line. If a custom title is longer than fits, it wraps to two
             lines (still balanced) — which is the acceptable fallback per the
             Aion Management brief. */
          .hero-title { font-size: 42px; max-width: min(92vw, 60rem); }
        }
        @media (min-width: 1024px) {
          .hero-title { font-size: 50px; max-width: min(92vw, 72rem); }
        }

        /* Subtitle sits directly beneath the h1 title. Kept lighter and
           smaller so the h1 stays the primary anchor. */
        .hero-subtitle {
          font-family: var(--vibe-font-heading), sans-serif;
          font-size: 15px;
          font-weight: 400;
          line-height: 1.35;
          letter-spacing: 0.01em;
          color: rgba(255, 255, 255, 0.92);
          text-shadow: 0 2px 24px rgba(0, 0, 0, 0.25);
          /* Mobile: no bottom margin so justify-center can split space
             evenly between the header and the cards. */
          margin: 0 auto;
          max-width: 40ch;
        }
        .hero-subtitle p,
        .hero-subtitle span {
          font: inherit;
          color: inherit;
          text-shadow: inherit;
          margin: 0;
        }
        /* Keep city + state as one unbreakable unit so a long place name
           wraps onto the second line intact instead of mid-word. */
        .hero-subtitle-location {
          white-space: nowrap;
        }
        @media (min-width: 768px) {
          .hero-subtitle {
            font-size: 20px;
            max-width: 46ch;
            margin-bottom: 1.25rem;
          }
        }
        @media (min-width: 1024px) {
          .hero-subtitle { font-size: 22px; }
        }

        /* ================================================================ */
        /* Bottom quick-link cards                                         */
        /* ================================================================ */
        .hero-cards {
          display: grid;
          grid-template-columns: 1fr;
          gap: 0.75rem;
          width: 100%;
          max-width: 78rem;
          /* Mobile: no top margin — equal header↔text↔cards spacing comes
             from .hero-center's justify-center. */
          margin: 0 auto;
        }
        @media (min-width: 768px) {
          .hero-cards {
            grid-template-columns: repeat(3, 1fr);
            gap: 1rem;
            margin-top: 6rem;
          }
        }
        /* Tablet: let the three cards extend a bit past .hero-content's
           inline padding so they fill more of the row (matches the wider
           look in the reference). Desktop keeps its centered 78rem max. */
        @media (min-width: 768px) and (max-width: 1023px) {
          .hero-cards {
            width: calc(100% + 2rem);
            max-width: none;
            margin-left: -1rem;
            margin-right: -1rem;
          }
        }
        .hero-card-cell {
          display: flex;
        }
        .hero-card {
          display: flex;
          flex-direction: column;
          gap: 0.4rem;
          width: 100%;
          padding: 1.1rem 1.35rem;
          text-align: left;
          text-decoration: none;
          color: #fff;
          border: 1px solid rgba(255, 255, 255, 0.45);
          border-radius: 0.5rem;
          /* Transparent by default; fill only on hover. */
          background: transparent;
          transition: background 0.3s ease, border-color 0.3s ease, transform 0.3s ease;
        }
        .hero-card:hover {
          background: var(--vibe-color-primary);
          border-color: var(--vibe-color-primary);
          color: #fff;
          transform: translateY(-2px);
        }
        .hero-card:hover .hero-card-heading,
        .hero-card:hover .hero-card-text {
          color: #fff;
        }
        .hero-card:focus-visible {
          outline: 2px solid #fff;
          outline-offset: 3px;
        }
        .hero-card-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
        }
        .hero-card-heading {
          font-family: var(--vibe-font-heading), sans-serif;
          font-size: 1.05rem;
          font-weight: 700;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          line-height: 1.1;
          white-space: nowrap;
        }
        @media (min-width: 1024px) {
          .hero-card-heading { font-size: 1.15rem; }
        }
        /* Tablet: tighten the heading↔arrow gap a touch so the single-line
           heading has room next to the fixed-width arrow. */
        @media (min-width: 768px) and (max-width: 1023px) {
          .hero-card-head { gap: 0.5rem; }
        }
        .hero-card-arrow {
          width: 2.25rem;
          height: 0.9rem;
          flex-shrink: 0;
          transition: transform 0.3s ease;
        }
        .hero-card:hover .hero-card-arrow {
          transform: translateX(4px);
        }
        .hero-card-text {
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          line-height: 1.4;
          color: rgba(255, 255, 255, 0.88);
        }

        @media (prefers-reduced-motion: reduce) {
          .hero-entrance { animation: none; opacity: 1; transform: none; }
          .hero-card:hover { transform: none; }
          .hero-card:hover .hero-card-arrow { transform: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Hero',
  displayName: 'Hero Section',
  tokens: {
    'hero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>The Ultimate Student Living Experience</p>',
    },
    'hero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue:
        '<p><span data-type="field-token" data-field="property_bedrooms_range"></span> Bedroom Apartments in <span class="hero-subtitle-location"><span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span></span></p>',
    },
  },
  slotGroups: {
    'hero.bg_image': bgDef,
    'hero.card_1': card1Def,
    'hero.card_2': card2Def,
    'hero.card_3': card3Def,
  },
};

(Hero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Hero;
